// v1
// Path: frontend/src/components/SyncHub/shared/DiffAnalyzerView.jsx
// Inputs: numerous props from FieldMappingDesigner
// Outputs: The Diff / Preview Analyzer sub-view for pipelines.
// Summary: Extracted DiffAnalyzerView to split FieldMappingDesigner.

import React from 'react';
import DiffMetricCards from './DiffMetricCards';

export default function DiffAnalyzerView(props) {
    const {
        loadingPreview,
        diffPreviewData,
        diffDetailTab,
        setDiffDetailTab,
        selectedQBEntity,
        handleGenerateDiff
    } = props;

    return (
        <div className="flex-1 overflow-y-auto bg-slate-900 p-4">
            <div className="max-w-4xl mx-auto space-y-4">
                <div className="flex justify-between items-center bg-slate-850 p-4 rounded-xl border border-slate-750">
                    <div>
                        <h4 className="text-sm font-bold text-white">Target Impact Analysis</h4>
                        <p className="text-[11px] text-slate-400 mt-1">
                            Analyzes Passport source data against existing {selectedQBEntity} records to calculate updates and creations.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={handleGenerateDiff}
                        disabled={loadingPreview}
                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-xs font-bold transition-colors flex items-center gap-2 shadow-md disabled:opacity-50"
                    >
                        {loadingPreview ? (
                            <>
                                <span className="material-symbols-outlined text-[16px] animate-spin">sync</span>
                                Analyzing...
                            </>
                        ) : (
                            <>
                                <span className="material-symbols-outlined text-[16px]">troubleshoot</span>
                                Generate Impact Diff
                            </>
                        )}
                    </button>
                </div>

                {loadingPreview && (
                    <div className="p-8 text-center bg-slate-850 border border-slate-750 rounded-xl">
                        <span className="material-symbols-outlined text-3xl text-indigo-400 animate-spin mb-3">sync</span>
                        <p className="text-slate-300 font-medium">Analyzing records...</p>
                        <p className="text-slate-500 text-xs mt-2">This may take a moment depending on the data size.</p>
                    </div>
                )}

                {!loadingPreview && !diffPreviewData && (
                    <div className="p-8 text-center bg-slate-850 border border-slate-750 border-dashed rounded-xl">
                        <span className="material-symbols-outlined text-4xl text-slate-600 mb-3">data_check</span>
                        <p className="text-slate-400">Click "Generate Impact Diff" to evaluate record changes.</p>
                    </div>
                )}

                {!loadingPreview && diffPreviewData && (
                    <div className="space-y-4">
                        {/* Use extracted DiffMetricCards component */}
                        <DiffMetricCards diffPreviewData={diffPreviewData} />

                        {/* Details Panel */}
                        <div className="bg-slate-950 border border-slate-800 rounded-xl overflow-hidden shadow-lg">
                            <div className="flex bg-slate-900 border-b border-slate-800 p-1">
                                <button
                                    type="button"
                                    onClick={() => setDiffDetailTab('changes')}
                                    className={`px-4 py-2 text-xs font-bold rounded-lg transition-colors ${diffDetailTab === 'changes' ? 'bg-indigo-500/20 text-indigo-300' : 'text-slate-400 hover:text-slate-300 hover:bg-slate-800'}`}
                                >
                                    Calculated Changes
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setDiffDetailTab('qb_sample')}
                                    className={`px-4 py-2 text-xs font-bold rounded-lg transition-colors ${diffDetailTab === 'qb_sample' ? 'bg-indigo-500/20 text-indigo-300' : 'text-slate-400 hover:text-slate-300 hover:bg-slate-800'}`}
                                >
                                    Target Sample Objects
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setDiffDetailTab('pbs_sample')}
                                    className={`px-4 py-2 text-xs font-bold rounded-lg transition-colors ${diffDetailTab === 'pbs_sample' ? 'bg-indigo-500/20 text-indigo-300' : 'text-slate-400 hover:text-slate-300 hover:bg-slate-800'}`}
                                >
                                    Source ERP Sample
                                </button>
                            </div>
                            <div className="p-4">
                                {diffDetailTab === 'changes' && (
                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center">
                                            <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider">
                                                Transformations & Detailed Field Comparison ({diffPreviewData.sampleMappings?.length || 0})
                                            </h4>
                                            <span className="text-[11px] text-slate-400">
                                                Comparing mapped incoming values directly against destination target values
                                            </span>
                                        </div>
                                        <div className="space-y-3">
                                            {(diffPreviewData.sampleMappings || []).map((sample, idx) => {
                                                const actionColor = sample.action === 'CREATE'
                                                    ? 'bg-emerald-950 text-emerald-300 border-emerald-800'
                                                    : sample.action === 'UPDATE'
                                                        ? 'bg-amber-950 text-amber-300 border-amber-800'
                                                        : 'bg-blue-950 text-blue-300 border-blue-800';

                                                return (
                                                    <div key={idx} className="bg-slate-850 border border-slate-750 rounded-xl p-3 text-xs space-y-2">
                                                        <div className="flex justify-between items-center border-b border-slate-750 pb-2">
                                                            <div className="flex items-center gap-2">
                                                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${actionColor}`}>
                                                                    {sample.action === 'UNCHANGED' ? 'IN SYNC (0 CHANGES)' : sample.action}
                                                                </span>
                                                                <span className="font-mono font-bold text-white">{sample.sourceKey}</span>
                                                                <span className="text-slate-400">&bull;</span>
                                                                <span className="text-slate-200 font-medium">{sample.sourceName}</span>
                                                            </div>
                                                            {sample.changedFields && sample.changedFields.length > 0 && (
                                                                <span className="text-[11px] text-amber-400 font-mono">
                                                                    {sample.changedFields.length} field{sample.changedFields.length === 1 ? '' : 's'} changing: {sample.changedFields.join(', ')}
                                                                </span>
                                                            )}
                                                            {sample.action === 'UNCHANGED' && (
                                                                <span className="text-[11px] text-blue-400">
                                                                    ✓ All destination fields already match incoming ERP data
                                                                </span>
                                                            )}
                                                        </div>

                                                        {/* If field deltas exist, render side-by-side comparison table */}
                                                        {sample.fieldDeltas && sample.fieldDeltas.length > 0 ? (
                                                            <div className="border border-slate-800 rounded-lg overflow-hidden my-2">
                                                                <table className="w-full text-left border-collapse text-[11px]">
                                                                    <thead>
                                                                        <tr className="bg-slate-900/90 text-slate-400 border-b border-slate-800">
                                                                            <th className="py-1 px-3 w-1/4">Target Field</th>
                                                                            <th className="py-1 px-3 w-1/3">Current Value in Target</th>
                                                                            <th className="py-1 px-2 text-center w-8"></th>
                                                                            <th className="py-1 px-3 w-1/3">New Incoming Value</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody className="divide-y divide-slate-800/60 font-mono">
                                                                        {sample.fieldDeltas.map((d, dIdx) => (
                                                                            <tr key={dIdx} className={d.isChanged ? 'bg-amber-950/20' : 'bg-slate-950/40 text-slate-400'}>
                                                                                <td className="py-1.5 px-3 font-semibold text-slate-300">
                                                                                    {d.field}
                                                                                </td>
                                                                                <td className={`py-1.5 px-3 truncate max-w-xs ${d.isChanged ? 'text-rose-300 line-through' : 'text-slate-400'}`} title={d.currentValue}>
                                                                                    {d.currentValue}
                                                                                </td>
                                                                                <td className="py-1.5 px-2 text-center font-sans font-bold text-slate-500">
                                                                                    ➔
                                                                                </td>
                                                                                <td className={`py-1.5 px-3 truncate max-w-xs ${d.isChanged ? 'text-emerald-300 font-bold' : 'text-slate-400'}`} title={d.newValue}>
                                                                                    {d.newValue}
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        ) : (
                                                            <pre className="p-2.5 bg-slate-950 rounded text-[11px] font-mono text-sky-300 overflow-x-auto border border-slate-800">
                                                                {JSON.stringify(sample.mappedPayload, null, 2)}
                                                            </pre>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {diffDetailTab === 'qb_sample' && (
                                    <div className="space-y-2">
                                        <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider">
                                            Target Destination Sample Objects ({diffPreviewData.qbSampleRecords?.length || 0})
                                        </h4>
                                        <pre className="p-3 bg-slate-950 rounded-xl text-[11px] font-mono text-purple-300 overflow-x-auto border border-slate-800 max-h-96">
                                            {JSON.stringify(diffPreviewData.qbSampleRecords || [], null, 2)}
                                        </pre>
                                    </div>
                                )}

                                {diffDetailTab === 'pbs_sample' && (
                                    <div className="space-y-2">
                                        <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider">
                                            Source ERP Export Extract ({diffPreviewData.pbsSampleRecords?.length || 0})
                                        </h4>
                                        <pre className="p-3 bg-slate-950 rounded-xl text-[11px] font-mono text-emerald-300 overflow-x-auto border border-slate-800 max-h-96">
                                            {JSON.stringify(diffPreviewData.pbsSampleRecords || [], null, 2)}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
